import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/app/page-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { apiV2 } from '@/lib/api-v2-client'
import { isVisualRegression } from '@/lib/incident'
import { StatusBadge } from '@/components/status-badge'
import { toast } from 'sonner'
import { listAllMonitors } from '@/lib/list-all-monitors'
import { CatSleeping } from '@/components/mascot'
import { ErrorState } from '@/components/state-view'
import { formatMs } from '@/lib/format-duration'

export const Route = createFileRoute('/_authenticated/dashboard/incidents/')({
  component: IncidentsPage,
})

function IncidentsPage() {
  const queryClient = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)

  const { data: incidents, isLoading, isError, refetch } = useQuery({
    queryKey: ['incidents'],
    queryFn: () => apiV2.listIncidents(),
    refetchInterval: 30_000,
  })

  const resolveMutation = useMutation({
    mutationFn: (id: string) => apiV2.resolveIncident(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incidents'] })
      toast.success('Incident resolved')
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to resolve')
    },
  })

  const allIncidents = incidents?.data ?? []
  const ongoingIncidents = allIncidents.filter((i) => !i.resolved_at)
  const resolvedIncidents = allIncidents.filter((i) => i.resolved_at)

  return (
    <div className="space-y-6">
      <PageHeader
        title="Incidents"
        description="View and manage service incidents"
        primaryAction={<Button onClick={() => setShowCreate(true)}>Create incident</Button>}
      />

      {showCreate && <CreateIncidentForm onClose={() => setShowCreate(false)} />}

      {/* A failed request is not an empty list. With old data on screen, a failed refresh keeps it. */}
      {isError && !incidents ? (
        <ErrorState
          title="Could not load incidents"
          message="The request failed. Your incidents are safe. Try again."
          onRetry={() => void refetch()}
          retryLabel="Retry"
        />
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Ongoing Incidents
                {ongoingIncidents.length > 0 && (
                  <Badge variant="destructive">{ongoingIncidents.length}</Badge>
                )}
              </CardTitle>
              <CardDescription>
                Active incidents requiring attention
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-16 animate-pulse rounded bg-muted" />
                  ))}
                </div>
              ) : ongoingIncidents.length > 0 ? (
                <div className="space-y-4">
                  {ongoingIncidents.map((incident) => (
                    <div
                      key={incident.id}
                      className="flex flex-col gap-3 rounded-lg border border-status-down/30 bg-status-down-soft p-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <Link
                        to="/dashboard/incidents/$id"
                        params={{ id: incident.id }}
                        className="min-w-0 flex-1 space-y-1"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{incident.monitor_name}</span>
                          {isVisualRegression(incident.cause) ? (
                            <StatusBadge kind="degraded" label="Visual change" />
                          ) : (
                            <Badge variant="destructive">Down</Badge>
                          )}
                        </div>
                        <p className="text-sm text-body">
                          Started {incident.started_at ? new Date(incident.started_at).toLocaleString() : 'recently'}
                        </p>
                      </Link>
                      <div className="flex items-center gap-4">
                        <div className="text-sm sm:text-right">
                          <div className="text-body">Duration</div>
                          <div className="font-medium">
                            {incident.started_at ? formatDuration(new Date(incident.started_at)) : '—'}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resolveMutation.mutate(incident.id)}
                          disabled={resolveMutation.isPending}
                        >
                          Resolve
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <CatSleeping size={100} className="mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    No ongoing incidents. All systems operational.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Resolved Incidents</CardTitle>
              <CardDescription>
                Past incidents that have been resolved
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 animate-pulse rounded bg-muted" />
                  ))}
                </div>
              ) : resolvedIncidents.length > 0 ? (
                <div className="space-y-4">
                  {resolvedIncidents.map((incident) => (
                    <Link
                      key={incident.id}
                      to="/dashboard/incidents/$id"
                      params={{ id: incident.id }}
                      className="flex items-center justify-between rounded-lg border p-4 hover:bg-muted/50"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{incident.monitor_name}</span>
                          <Badge variant="secondary">Resolved</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {incident.started_at ? new Date(incident.started_at).toLocaleString() : 'Unknown'}
                        </p>
                      </div>
                      <div className="text-right text-sm text-muted-foreground">
                        Duration:{' '}
                        {incident.resolved_at && incident.started_at
                          ? formatDurationBetween(
                              new Date(incident.started_at),
                              new Date(incident.resolved_at)
                            )
                          : 'N/A'}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="py-8 text-center text-muted-foreground">
                  No past incidents recorded
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}

function CreateIncidentForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [monitorId, setMonitorId] = useState('')
  const [cause, setCause] = useState('')

  const { data: monitorsResponse } = useQuery({
    queryKey: ['monitors', 'all'],
    queryFn: () => listAllMonitors(),
  })

  const monitors = monitorsResponse?.data ?? []

  const createMutation = useMutation({
    mutationFn: () => apiV2.createIncident({ monitor_id: monitorId, cause: cause || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incidents'] })
      toast.success('Incident created')
      onClose()
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to create incident')
    },
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create Incident</CardTitle>
        <CardDescription>Manually report a service incident</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            createMutation.mutate()
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="monitor">Monitor</Label>
            <Select value={monitorId} onValueChange={setMonitorId} required>
              <SelectTrigger id="monitor">
                <SelectValue placeholder="Select a monitor" />
              </SelectTrigger>
              <SelectContent>
                {monitors.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="cause">Cause (optional)</Label>
            <Input
              id="cause"
              value={cause}
              onChange={(e) => setCause(e.target.value)}
              placeholder="Brief description of the issue"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={!monitorId || createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Incident'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

function formatDuration(startDate: Date): string {
  return formatMs(Date.now() - startDate.getTime())
}

function formatDurationBetween(start: Date, end: Date): string {
  const diff = end.getTime() - start.getTime()
  return formatMs(diff)
}
